/**
 * Calculate 3D tilt rotation values based on mouse position relative to an element.
 * Used for interactive card/panel effects.
 */

export interface TiltResult {
	rotateX: number;
	rotateY: number;
}

export interface TiltOptions {
	/** Maximum rotation angle in degrees (default: 4) */
	maxAngle?: number;
}

/**
 * Calculate tilt rotation based on mouse position within element bounds.
 * @param e - Mouse event
 * @param element - The element to calculate tilt for
 * @param options - Configuration options
 */
export function calculateTilt(
	e: MouseEvent,
	element: HTMLElement,
	options: TiltOptions = {}
): TiltResult {
	const { maxAngle = 4 } = options;
	const rect = element.getBoundingClientRect();

	const x = e.clientX - rect.left;
	const y = e.clientY - rect.top;
	const centerX = rect.width / 2;
	const centerY = rect.height / 2;

	return {
		rotateX: -((y - centerY) / centerY) * maxAngle,
		rotateY: ((x - centerX) / centerX) * maxAngle
	};
}

/**
 * Calculate glow position as percentages for radial gradients.
 */
export function calculateGlowPosition(
	e: MouseEvent,
	element: HTMLElement
): { x: number; y: number } {
	const rect = element.getBoundingClientRect();
	return {
		x: ((e.clientX - rect.left) / rect.width) * 100,
		y: ((e.clientY - rect.top) / rect.height) * 100
	};
}

/** Reset tilt values to zero */
export const TILT_RESET: TiltResult = { rotateX: 0, rotateY: 0 };
